import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { trackEvent, Events } from "@/lib/analytics";
import { useNavigate, Link } from "react-router-dom";
import { Waveform, EnvelopeSimple, Lock, GoogleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      trackEvent(Events.LOGIN_COMPLETED, { method: "email" });
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#0A0A0C" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Waveform size={36} weight="bold" style={{ color: "#F59E0B" }} />
            <h1 className="text-3xl font-light tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Daremusics</h1>
          </div>
          <p className="text-base" style={{ color: "#A1A1AA", fontFamily: "'Manrope', sans-serif" }}>Sign in to create Telugu songs</p>
        </div>

        <div className="bg-[#141417] border border-white/5 rounded-2xl p-6 md:p-8 space-y-5">
          {/* Google Login Button */}
          <button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 bg-white text-black font-semibold rounded-full px-8 py-3 hover:bg-gray-100 hover:-translate-y-0.5 transition-all duration-300"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            <GoogleLogo size={20} weight="bold" />
            Continue with Google
          </button>

          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs uppercase tracking-wider" style={{ color: "#A1A1AA" }}>or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2" style={{ color: "#F59E0B" }}>Email</label>
              <div className="bg-[#0A0A0C] border border-white/10 rounded-xl focus-within:border-[#F59E0B] transition-colors flex items-center gap-3 px-4">
                <EnvelopeSimple size={18} style={{ color: "#A1A1AA" }} />
                <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full bg-transparent py-3 text-white placeholder:text-gray-500 outline-none" style={{ fontFamily: "'Manrope', sans-serif" }} required />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2" style={{ color: "#F59E0B" }}>Password</label>
              <div className="bg-[#0A0A0C] border border-white/10 rounded-xl focus-within:border-[#F59E0B] transition-colors flex items-center gap-3 px-4">
                <Lock size={18} style={{ color: "#A1A1AA" }} />
                <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="w-full bg-transparent py-3 text-white placeholder:text-gray-500 outline-none" style={{ fontFamily: "'Manrope', sans-serif" }} required />
              </div>
            </div>
            <button data-testid="login-submit-btn" type="submit" disabled={loading} className="bg-[#F59E0B] text-black font-semibold rounded-full px-8 py-3 hover:bg-[#D97706] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_20px_rgba(245,158,11,0.2)] disabled:opacity-50 w-full flex items-center justify-center gap-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {loading ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : null}
              Sign In
            </button>
          </form>
          <p className="text-center text-sm" style={{ color: "#A1A1AA", fontFamily: "'Manrope', sans-serif" }}>
            <Link to="/forgot-password" className="hover:text-white transition-colors" style={{ color: "#F59E0B" }}>Forgot Password?</Link>
          </p>
          <p className="text-center text-sm" style={{ color: "#A1A1AA", fontFamily: "'Manrope', sans-serif" }}>
            Don't have an account? <Link to="/register" className="hover:text-white transition-colors" style={{ color: "#F59E0B" }}>Sign Up</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
